import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './AthleteUserEntriesSelectionValidation';

import NavLinks from '../../../meet/NavLinks';
import EntryNavLinks from '../../EntryNavLinks';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOrgUnitAthleteEntryInfoData from '../../../../state/orgUnitAthleteEntryInfo/UseOrgUnitAthleteEntryInfoData';
import useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/omeMeetOrgUnitAthleteEntryIndividualRosterEntry/UseOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData';
import useOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/orgUnitAthleteEntry/UseOrgUnitAthleteEntryIndividualRosterEntryData';

import useMeetData from '@usas/ui-web/state/meet/UseMeetData';
import useEventData from '@usas/ui-web/state/event/UseEventData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';

import Constants from '@usas/ui-web/utils/Constants';
import UseForm from '@usas/ui-web/utils/UseForm';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_FORM_STATE = {
  eventId: Constants.DEFAULT_ID,
  eventName: '',
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  event: {},
  modalTitle: 'Continue Without Saving?',
};

const useAthleteUserEntriesBonusIndividualSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventState, setEventState } = useEventData();
  const { meetState } = useMeetData();
  const { omeMeetState } = useOmeMeetData();
  const { orgUnitAthleteEntryInfoState, getOrgUnitAthleteEntryInfo } = useOrgUnitAthleteEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, getOrgUnitAthleteEntryIndividualRosterEntryByOrgUnitAthleteEntryId }
    = useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData();
  const { orgUnitAthleteEntryIndividualRosterEntryState, getOrgUnitAthleteEntryBonusIndividualRosterEntry,
    putOrgUnitAthleteEntryAthleteEntryIndividualRosterEntry } = useOrgUnitAthleteEntryIndividualRosterEntryData();
  const { HIDE_OME_ELIGIBILITY_50_BONUS_OPTION } = useEnvironmentVariableData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const [state, setState] = useState({ tryGet: false, tryRedirect: false, tryFilter: false, bonusEvents: '', sessions: [] });
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [gridState, setGridState] = useState({ gridData: [] });
  const [selectionChangesState, setSelectionChangesState] = useState({ arrayData: [] });
  const { handleSubmit, formState, errorState, setErrors, setFormState, onFormValueChanged, onValueTextPairChanged }
    = UseForm(INITIAL_FORM_STATE, submitFormCallback, validate);

  const onAddEditOverrideEntryClicked = (e, event) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.ATHLETE_ENTRIES_BONUS_INDIVIDUAL_OVERRIDE, { state: { event: event ? event : modalState.event } });
    }
    else {
      //Meet Route
      navigate(NavLinks.ATHLETE_ENTRIES_BONUS_INDIVIDUAL_OVERRIDE, { state: { event: event ? event : modalState.event } });
    }
  };

  const onOpenModalClicked = (e, event) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    //Editing unsaved override time, don't check for unsaved override when showing error msg
    if (event.swimTimeEntryOverride === true && event.individualAthleteMeetEventId === null) {
      if (selectionChangesState.arrayData?.length > 0) {
        setModalState({
          ...modalState,
          event: event,
          displayPopUp: true
        });
      }
      else {
        onAddEditOverrideEntryClicked(e, event);
      }
    }
    else {
      const unsavedOverrideIndex = orgUnitAthleteEntryIndividualRosterEntryState.arrayData?.events?.findIndex((x) => (x.swimTimeEntryOverride === true && x.individualAthleteMeetEventId === null));
      if (selectionChangesState.arrayData?.length > 0 || unsavedOverrideIndex > -1) {
        setModalState({
          ...modalState,
          event: event,
          displayPopUp: true
        });
      }
      else {
        onAddEditOverrideEntryClicked(e, event);
      }
    }
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onFilterClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    handleSubmit();
  };

  const onClearFilterClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setFormState(INITIAL_FORM_STATE);
    setState({ ...state, tryFilter: true });
    setErrors({});
  };

  const onSaveClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    //Check to see if the selections exceed any host event entry limits
    let previousAthleteEventCount = omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events?.length || 0;
    let previousAthleteEventCountAllOmeMeetEntry = previousAthleteEventCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryTotalEventsAdded;
    let updatedAthleteEventsArray = omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events ? JSON.parse(JSON.stringify(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events)) : [];
    let updatedAthleteEventsToExcludeArray = [];
    let updatedAthleteEventsToExcludeArrayAllOmeMeetEntry = [];
    let currentAthleteIndividualEventCount = 0;
    let currentAthleteIndividualEventCountAllOmeMeetEntry = 0;
    let currentAthleteBonusIndividualEventCount = 0;
    let currentAthleteBonusIndividualEventCountAllOmeMeetEntry = 0;
    let currentAthleteBonusEventsToExcludeCount = 0;
    let currentAthleteBonusEventsToExcludeCountAllOmeMeetEntry = 0;
    let exceededMaxEventsPerSessionLimitAllOmeMeetEntry = false;
    let exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray = [];

    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events) {
      for (let i = 0; i < omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events?.length; i++) {
        if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events[i].qualifiedAsBonus === false) {
          currentAthleteIndividualEventCount += 1;
        }
        else if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events[i].qualifiedAsBonus === true) {
          currentAthleteBonusIndividualEventCount += 1;

          if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 && orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal !== null) {
            if (orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal.events?.find(event => event.eventId === omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events[i].eventId)) {
              currentAthleteBonusEventsToExcludeCount += 1;
              updatedAthleteEventsToExcludeArray.push(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.events[i]);
            }
          }
        }
      }
    }

    currentAthleteIndividualEventCountAllOmeMeetEntry = currentAthleteIndividualEventCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryIndividualEventsAdded;
    currentAthleteBonusIndividualEventCountAllOmeMeetEntry = currentAthleteBonusIndividualEventCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryBonusEventsAdded;
    currentAthleteBonusEventsToExcludeCountAllOmeMeetEntry = currentAthleteBonusEventsToExcludeCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEventsAdded;

    //figure out event counts by type based on the selections
    let addedAthleteEventCount = 0;
    let removedAthleteEventCount = 0;
    let addedAthleteBonusEventsToExcludeCount = 0;
    let removedAthleteBonusEventsToExcludeCount = 0;
    let updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry = 0; //Bonus Event Entry to EXCLUDE Count across all OME Meet Entries
    let updatedAthleteEventCountAllOmeMeetEntry = 0; //Individual and Bonus Event Entry combined Count across all OME Meet Entries
    let updatedAthleteBonusEventCountAllOmeMeetEntry = 0; //Bonus Event Entry only Count across all OME Meet Entries
    let updatedAthleteBonusEventCountAllOmeMeetEntryAfterSubtractingUpdatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry = 0; //Bonus Event Entry only Count (after subtracting Bonus Event Entry to EXCLUDE Count) across all OME Meet Entries

    for (let i = 0; i < selectionChangesState.arrayData.length; i++) {
      if (selectionChangesState.arrayData[i].isSelected === true && !selectionChangesState.arrayData[i].individualAthleteMeetEventId) {
        addedAthleteEventCount += 1;
        updatedAthleteEventsArray.push(selectionChangesState.arrayData[i]);

        if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 && orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal !== null) {
          if (orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal.events?.find(event => event.eventId === selectionChangesState.arrayData[i].eventId)) {
            addedAthleteBonusEventsToExcludeCount += 1;
            updatedAthleteEventsToExcludeArray.push(selectionChangesState.arrayData[i]);
          }
        }
      }
      else if (selectionChangesState.arrayData[i].isSelected === false && selectionChangesState.arrayData[i].individualAthleteMeetEventId) {
        removedAthleteEventCount += 1;
        const index = updatedAthleteEventsArray.findIndex(x => x.meetEventId === selectionChangesState.arrayData[i].meetEventId);
        if (index > -1) {
          updatedAthleteEventsArray.splice(index, 1);
        }

        if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 && orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal !== null) {
          if (orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal.events?.find(event => event.eventId === selectionChangesState.arrayData[i].eventId)) {
            removedAthleteBonusEventsToExcludeCount += 1;
            const index = updatedAthleteEventsToExcludeArray.findIndex(x => x.meetEventId === selectionChangesState.arrayData[i].meetEventId);
            if (index > -1) {
              updatedAthleteEventsToExcludeArray.splice(index, 1);
            }
          }
        }
      }
    }

    updatedAthleteEventCountAllOmeMeetEntry = previousAthleteEventCountAllOmeMeetEntry + addedAthleteEventCount - removedAthleteEventCount;
    updatedAthleteBonusEventCountAllOmeMeetEntry = currentAthleteBonusIndividualEventCountAllOmeMeetEntry + addedAthleteEventCount - removedAthleteEventCount;
    updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry = currentAthleteBonusEventsToExcludeCountAllOmeMeetEntry + addedAthleteBonusEventsToExcludeCount - removedAthleteBonusEventsToExcludeCount;
    updatedAthleteBonusEventCountAllOmeMeetEntryAfterSubtractingUpdatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry = updatedAthleteBonusEventCountAllOmeMeetEntry - updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry;
    updatedAthleteEventsToExcludeArrayAllOmeMeetEntry = orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEvents !== null ? updatedAthleteEventsToExcludeArray.concat(orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEvents) : updatedAthleteEventsToExcludeArray;

    let exceededBonusEventsToExcludeLimit = false;
    let exceededBonusEventsToExcludeLimitErrorMsg = '';

    if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 && orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal !== null &&
      (updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry > orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal?.eventsAmount)) {
      exceededBonusEventsToExcludeLimit = true;
      exceededBonusEventsToExcludeLimitErrorMsg = `The current selections include ${updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry} individual bonus ${updatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry === 1 ? 'event entry' : 'event entries'} ${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.length > 0 ? `(${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.map(event => event.eventName).join(', ')}) that are excluded from the individual bonus ${orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal?.eventsAmount === 1 ? 'event entry' : 'event entries'} count. Only ${orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal?.eventsAmount} individual bonus ${orgUnitAthleteEntryIndividualRosterEntryState.arrayData.bonusExcludeFromTotal?.eventsAmount === 1 ? 'event entry' : 'event entries'} that are excluded from the individual bonus event entries count are allowed.` : ''}`;
    }

    //check to see if the selections violate the max events per session limit, if the host set one
    if (orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerSession) {
      for (let i = 0; i < state.sessions.length; i++) {
        let eventsPerSessionCount = 0;
        let individualEventsPerSessionCount = 0;
        let bonusEventsPerSessionCount = 0;
        for (let j = 0; j < updatedAthleteEventsArray.length; j++) {
          if (updatedAthleteEventsArray[j].meetSessionId === state.sessions[i].id) {
            eventsPerSessionCount += 1;
            if (updatedAthleteEventsArray[j].qualifiedAsBonus === true) {
              bonusEventsPerSessionCount += 1;
            }
            else {
              individualEventsPerSessionCount += 1;
            }
          }
        }

        let eventsPerSessionCountAllOmeMeetEntry = eventsPerSessionCount;
        let individualEventsPerSessionCountAllOmeMeetEntry = individualEventsPerSessionCount;
        let bonusEventsPerSessionCountAllOmeMeetEntry = bonusEventsPerSessionCount;
        for (let j = 0; j < orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntrySessionsEventAdded?.length; j++) {
          if (orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntrySessionsEventAdded[j].meetSessionId === state.sessions[i].id) {
            eventsPerSessionCountAllOmeMeetEntry += orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntrySessionsEventAdded[j].totalEvents;
            bonusEventsPerSessionCountAllOmeMeetEntry += orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntrySessionsEventAdded[j].bonusEvents;
            individualEventsPerSessionCountAllOmeMeetEntry += orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntrySessionsEventAdded[j].individualEvents;
          }
        }
        if (eventsPerSessionCountAllOmeMeetEntry > orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerSession) {
          exceededMaxEventsPerSessionLimitAllOmeMeetEntry = true;
          exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray.push({
            meetSessionName: state.sessions[i].name, eventsPerSessionCount: eventsPerSessionCount, eventsPerSessionCountAllOmeMeetEntry: eventsPerSessionCountAllOmeMeetEntry,
            individualEventsPerSessionCount: individualEventsPerSessionCount, individualEventsPerSessionCountAllOmeMeetEntry: individualEventsPerSessionCountAllOmeMeetEntry, bonusEventsPerSessionCount: bonusEventsPerSessionCount, bonusEventsPerSessionCountAllOmeMeetEntry: bonusEventsPerSessionCountAllOmeMeetEntry
          });
        }
      }
    }

    //check to see if any bonus rules are violated by selections
    let bonusRuleViolated = false;
    let bonusRuleViolatedErrorMsg = '';
    for (let i = 0; i < omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus.length; i++) {
      //find the bonus rule that applies to the current number of individual (non-bonus) event entries
      if (currentAthleteIndividualEventCountAllOmeMeetEntry <= omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].maxQualifyingEvents &&
        currentAthleteIndividualEventCountAllOmeMeetEntry >= omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].minQualifyingEvents) {
        if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1) {
          if (updatedAthleteBonusEventCountAllOmeMeetEntryAfterSubtractingUpdatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry > omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount) {
            bonusRuleViolated = true;
            bonusRuleViolatedErrorMsg = `This athlete currently has ${currentAthleteIndividualEventCountAllOmeMeetEntry} individual event ${currentAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}. Based on the bonus event rules of the meet, an athlete with ${currentAthleteIndividualEventCountAllOmeMeetEntry} individual event ${currentAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} is allowed ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount} individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'}.\n${updatedAthleteBonusEventCountAllOmeMeetEntryAfterSubtractingUpdatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry} individual bonus event ${updatedAthleteBonusEventCountAllOmeMeetEntryAfterSubtractingUpdatedAthleteBonusEventExclude50sOfStrokeCountAllOmeMeetEntry === 1 ? 'entry is' : 'entries are'} currently selected, which is not allowed.${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.length > 0 ? ` (${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.map(event => event.eventName).join(', ')} are excluded from the allowed individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'} count).` : ''}`
          }
        }
        else {
          if (updatedAthleteBonusEventCountAllOmeMeetEntry > omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount) {
            bonusRuleViolated = true;
            bonusRuleViolatedErrorMsg = `This athlete currently has ${currentAthleteIndividualEventCountAllOmeMeetEntry} individual event ${currentAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}. Based on the bonus event rules of the meet, an athlete with ${currentAthleteIndividualEventCountAllOmeMeetEntry} individual event ${currentAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} is allowed ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount} individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'}.\n${updatedAthleteBonusEventCountAllOmeMeetEntry} individual bonus event ${updatedAthleteBonusEventCountAllOmeMeetEntry === 1 ? 'entry is' : 'entries are'} currently selected, which is not allowed.`
          }
        }
      }
    }

    let deleteAfterPayment = false;
    //checking for delete after payment if the athlete is attached to a team that has submitted, or if they are an unattached athlete that has submitted their own entry
    if (orgUnitAthleteEntryInfoState.objData.teamEntryStatus === Constants.WORKFLOW_STATUS_SUBMITTED ||
      omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.status === Constants.WORKFLOW_STATUS_SUBMITTED) {
      for (let i = 0; i < selectionChangesState.arrayData.length; i++) {
        if (selectionChangesState.arrayData[i].individualAthleteMeetEventId !== null && selectionChangesState.arrayData[i].isSelected === false &&
          selectionChangesState.arrayData[i].hasPaid === true && selectionChangesState.arrayData[i].canBeDeleted === false) {
          deleteAfterPayment = true;
        }
      }
    }

    //Throw any errors
    if (orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerMeet && updatedAthleteEventCountAllOmeMeetEntry > orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerMeet) {
      setErrors({ ...errorState, general: `The current selections exceed the individual event entry limit set by the meet host. Only ${orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerMeet} individual event ${orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerMeet === 1 ? 'entry is' : 'entries are'} allowed (including bonus entries). This athlete has ${currentAthleteIndividualEventCountAllOmeMeetEntry} individual event ${currentAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}, and ${updatedAthleteBonusEventCountAllOmeMeetEntry} individual bonus event ${updatedAthleteBonusEventCountAllOmeMeetEntry === 1 ? 'entry is' : 'entries are'} currently selected.` });
    }
    else if (orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerSession && exceededMaxEventsPerSessionLimitAllOmeMeetEntry === true) {
      let sessionCountErrorMsg = '';
      for (let i = 0; i < exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray.length; i++) {
        sessionCountErrorMsg += sessionCountErrorMsg !== '' ? `,\n${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].meetSessionName}: This athlete has ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].individualEventsPerSessionCountAllOmeMeetEntry} individual event ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].individualEventsPerSessionCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} in this session, and ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].bonusEventsPerSessionCountAllOmeMeetEntry} individual bonus event ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].bonusEventsPerSessionCountAllOmeMeetEntry === 1 ? 'entry in this session is' : 'entries in this session are'} currently selected` : `\n${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].meetSessionName}. This athlete has ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].individualEventsPerSessionCountAllOmeMeetEntry} individual event ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].individualEventsPerSessionCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} in this session, and ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].bonusEventsPerSessionCountAllOmeMeetEntry} individual bonus event ${exceededMaxEventsPerSessionLimitAllOmeMeetEntryInfoArray[i].bonusEventsPerSessionCountAllOmeMeetEntry === 1 ? 'entry in this session is' : 'entries in this session are'} currently selected`;
      }
      setErrors({ ...errorState, general: `The current selections exceed the individual event entry per session limit set by the meet host. Only ${orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerSession} individual event ${orgUnitAthleteEntryInfoState.objData.maxEventsPerIndividualPerSession === 1 ? 'entry per session is' : 'entries per session are'} allowed (including bonus entries). Based on the current selections, this athlete has too many individual event entries in the following sessions: ${sessionCountErrorMsg}` });
    }
    else if (exceededBonusEventsToExcludeLimit === true) {
      setErrors({ ...errorState, general: exceededBonusEventsToExcludeLimitErrorMsg });
    }
    else if (bonusRuleViolated === true) {
      setErrors({ ...errorState, general: bonusRuleViolatedErrorMsg });
    }
    else if (deleteAfterPayment === true) {
      setErrors({ ...errorState, general: `The current selections removed an individual bonus event entry that has been paid for. Removing an individual bonus event entry after it has been purchased is not allowed.` });
    }
    else {
      if (selectionChangesState.arrayData.length > 0) {
        putOrgUnitAthleteEntryAthleteEntryIndividualRosterEntry(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.orgUnitAthleteEntryId, selectionChangesState.arrayData);
        setState({ ...state, tryGet: true });
      }
      else {
        setState({ ...state, tryGet: false, tryRedirect: true });
      }
    }
  };

  const onBackClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.ATHLETE_ENTRIES);
    }
    else {
      //Meet Route
      navigate(NavLinks.ATHLETE_ENTRIES);
    }
  }

  const onSelectAll = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const updatedArrayData = JSON.parse(JSON.stringify(selectionChangesState.arrayData));
    const updatedGridData = JSON.parse(JSON.stringify(gridState.gridData));
    for (let i = 0; i < updatedGridData.length; i++) {
      if (updatedGridData[i].hasQualifyingTime === true || updatedGridData[i].swimTimeEntryOverride === true) {
        if (updatedGridData[i].individualEventEntered === false) {
          if (updatedGridData[i].hasPaid === false || updatedGridData[i].canBeDeleted === true) {
            updatedGridData[i].isSelected = true;
          }
        }
        const selectedIndividualRosterEntryIndexArrayData = updatedArrayData.findIndex(x => x.meetEventId === updatedGridData[i].meetEventId);
        //Edit
        if (selectedIndividualRosterEntryIndexArrayData > -1) {
          if (updatedGridData[i].individualEventEntered === false) {
            if (updatedArrayData[selectedIndividualRosterEntryIndexArrayData].hasPaid === false ||
              updatedArrayData[selectedIndividualRosterEntryIndexArrayData].canBeDeleted === true) {
              updatedArrayData[selectedIndividualRosterEntryIndexArrayData].isSelected = true;
            }
          }
        }
        else {
          updatedArrayData.push(updatedGridData[i]);
        }
      }
    }

    setSelectionChangesState({
      ...selectionChangesState,
      arrayData: updatedArrayData
    });
    setGridState({ ...gridState, gridData: updatedGridData });
  }

  const onUnselectAll = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const updatedArrayData = JSON.parse(JSON.stringify(selectionChangesState.arrayData));
    const updatedGridData = JSON.parse(JSON.stringify(gridState.gridData));
    for (let i = 0; i < updatedGridData.length; i++) {
      if (updatedGridData[i].hasQualifyingTime === true || updatedGridData[i].swimTimeEntryOverride === true) {
        if (updatedGridData[i].individualEventEntered === false) {
          if (updatedGridData[i].hasPaid === false || updatedGridData[i].canBeDeleted === true) {
            updatedGridData[i].isSelected = false;
          }
        }
        const selectedIndividualRosterEntryIndexArrayData = updatedArrayData.findIndex(x => x.meetEventId === updatedGridData[i].meetEventId);
        //Edit
        if (selectedIndividualRosterEntryIndexArrayData > -1) {
          if (updatedGridData[i].individualEventEntered === false) {
            if (updatedArrayData[selectedIndividualRosterEntryIndexArrayData].hasPaid === false ||
              updatedArrayData[selectedIndividualRosterEntryIndexArrayData].canBeDeleted === true) {
              updatedArrayData[selectedIndividualRosterEntryIndexArrayData].isSelected = false;
            }
          }
        }
        else {
          updatedArrayData.push(updatedGridData[i]);
        }
      }
    }

    setSelectionChangesState({
      ...selectionChangesState,
      arrayData: updatedArrayData
    });
    setGridState({ ...gridState, gridData: updatedGridData });
  };

  const onCheckboxChange = (e, meetEventId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const updatedArrayData = JSON.parse(JSON.stringify(selectionChangesState.arrayData));
    const selectedIndividualRosterEntryIndexArrayData = updatedArrayData.findIndex(x => x.meetEventId === meetEventId);

    const updatedGridData = JSON.parse(JSON.stringify(gridState.gridData));
    const selectedIndividualRosterEntryIndexGridData = gridState.gridData?.findIndex(x => x.meetEventId === meetEventId);

    if (selectedIndividualRosterEntryIndexArrayData > -1) {
      updatedArrayData[selectedIndividualRosterEntryIndexArrayData].isSelected = !updatedArrayData[selectedIndividualRosterEntryIndexArrayData].isSelected;
      setSelectionChangesState({
        ...selectionChangesState,
        arrayData: updatedArrayData
      });
    }

    if (selectedIndividualRosterEntryIndexGridData > -1) {
      updatedGridData[selectedIndividualRosterEntryIndexGridData].isSelected = !updatedGridData[selectedIndividualRosterEntryIndexGridData].isSelected;
      setGridState({
        ...gridState,
        gridData: updatedGridData
      });
    }

    if (selectedIndividualRosterEntryIndexArrayData === -1) {
      const newIndividualRosterEntry = JSON.parse(JSON.stringify(updatedGridData[selectedIndividualRosterEntryIndexGridData]));
      updatedArrayData.push(newIndividualRosterEntry);
      setSelectionChangesState({
        ...selectionChangesState,
        arrayData: updatedArrayData
      });
    }
  };

  function submitFormCallback(formState) {
    setState({ ...state, tryFilter: true });
  }

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      const isSaved = selectionsHaveBeenSaved();
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.objData, selectionChangesState.arrayData]);

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0 && eventState.isArrayLoaded === true) {
      const allowedCourseIds = omeMeetState.objData?.omeMeetEntryCourse.map(x => { return x.courseId });
      const filteredArrayData = JSON.parse(JSON.stringify(eventState.arrayData)).filter(x => allowedCourseIds.some(courseId => courseId === x.courseId));
      const filteredArrayDataFormatted = filteredArrayData.map(x => { return { id: x.eventId, name: x.eventName }; });
      setEventState({ ...eventState, filteredItems: filteredArrayDataFormatted });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.objData, eventState.isArrayLoaded])

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoaded === true && Object.keys(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData).length > 0) {
      //Get session data for session error msgs
      let meetSessionCopy = [];
      if (meetState.objData.meetSession.length > 0) {
        meetSessionCopy = JSON.parse(JSON.stringify(meetState.objData?.meetSession));
        meetSessionCopy.sort(function (a, b) { return a.sessionOrderId - b.sessionOrderId });
      }
      const meetSessionCopyOptions = meetSessionCopy.map((session) => ({ id: session.meetSessionId, name: `${session.sessionOrderId || ''} (${session.sessionType?.typeName || ''} ${session.sessionDate ? formatDate(session.sessionDate) : ''})` }));
      meetSessionCopyOptions.unshift({ id: Constants.DEFAULT_ID, name: '--' });
      setState({ ...state, bonusEvents: orgUnitAthleteEntryInfoState.objData?.bonusEvents ?? '', sessions: meetSessionCopyOptions });

      if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.orgUnitAthleteEntryId) {
        if (location.state?.tryGet !== false) {
          getOrgUnitAthleteEntryBonusIndividualRosterEntry(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.orgUnitAthleteEntryId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (orgUnitAthleteEntryIndividualRosterEntryState.isArrayLoaded === true &&
      orgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading === false) {
      const athleteMaxNumberMet = orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteMaxNumberMet;
      if (athleteMaxNumberMet === true && (orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events === null
        || orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events?.length === 0)) {
        setErrors({ ...errorState, general: "The meet's max number of athletes cap has been reached. Individual bonus event entries are no longer allowed." })
      }
      const arrayDataCopy = orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events ? JSON.parse(JSON.stringify(orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events)) : [];
      //keep unsaved selections / unselections from previous searches
      if (selectionChangesState.arrayData.length > 0) {
        for (let i = 0; i < selectionChangesState.arrayData.length; i++) {
          const matchingIndex = arrayDataCopy.findIndex(x => x.meetEventId === selectionChangesState.arrayData[i].meetEventId &&
            x.qualifiedAsBonus === selectionChangesState.arrayData[i].qualifiedAsBonus);
          if (matchingIndex > -1) {
            arrayDataCopy[matchingIndex].isSelected = selectionChangesState.arrayData[i].isSelected;
          }
        }
      }
      if (formState.eventId > 0) {
        const filteredArrayDataCopy = arrayDataCopy.filter(x => x.eventId === formState.eventId && x.qualifiedAsBonus === true);
        setGridState({ ...gridState, gridData: filteredArrayDataCopy });
      }
      else {
        const filteredArrayDataCopy = arrayDataCopy.filter(x => x.qualifiedAsBonus === true);
        setGridState({ ...gridState, gridData: filteredArrayDataCopy });
      }
      if (state.tryFilter === true) {
        setState({ ...state, tryFilter: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnitAthleteEntryIndividualRosterEntryState.isArrayLoaded,
  orgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading,
  state.tryFilter])

  useEffect(() => {
    if (state.tryGet === true && orgUnitAthleteEntryInfoState.isObjLoading === false && orgUnitAthleteEntryIndividualRosterEntryState.isSaving === false) {
      getOrgUnitAthleteEntryInfo(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId);
      getOrgUnitAthleteEntryIndividualRosterEntryByOrgUnitAthleteEntryId(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData.orgUnitAthleteEntryId, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.omeMeetOrgUnitEntryId);
      setState({ ...state, tryGet: false, tryRedirect: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryGet, orgUnitAthleteEntryInfoState, orgUnitAthleteEntryIndividualRosterEntryState])

  useEffect(() => {
    if (state.tryRedirect === true && orgUnitAthleteEntryInfoState.isObjLoading === false && orgUnitAthleteEntryIndividualRosterEntryState.isSaving === false &&
      omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading === false && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoading === false) {
      if (omeMeetState.route === '') {
        //OME Route
        navigate(EntryNavLinks.ATHLETE_ENTRIES);
      }
      else {
        //Meet Route
        navigate(NavLinks.ATHLETE_ENTRIES);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryRedirect, orgUnitAthleteEntryInfoState, orgUnitAthleteEntryIndividualRosterEntryState, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState])

  function selectionsHaveBeenSaved() {
    //see if selection changes have been saved
    if (selectionChangesState.arrayData.length > 0) {
      return false;
    }

    return true;
  }

  return {
    state,
    gridState,
    orgUnitAthleteEntryInfoState,
    orgUnitAthleteEntryIndividualRosterEntryState,
    omeMeetOrgUnitAthleteEntryIndividualRosterEntryState,
    onSelectAll,
    onUnselectAll,
    onAddEditOverrideEntryClicked,
    onSaveClicked,
    onBackClicked,
    onCheckboxChange,
    onFilterClicked,
    onClearFilterClicked,
    onFormValueChanged,
    onValueTextPairChanged,
    formState,
    errorState,
    modalState,
    onOpenModalClicked,
    onModalCanceled
  };
}

export default useAthleteUserEntriesBonusIndividualSelection;